import { useState } from 'react';
import { Card, Button, Badge, toast } from '@/components/ui';
import { Play, Square, Pause, Clock, Trash2, Plus, Timer, PenLine, Pencil, Save, X } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import {
  useTimerActivo,
  useIniciarTimer,
  usePausarTimer,
  useReanudarTimer,
  useDetenerTimer,
  useRegistrosTiempo,
  useRegistrarTiempoManual,
  useActualizarRegistroTiempo,
  useEliminarRegistroTiempo,
} from '@/hooks/queries/useTiempo';

interface TiempoPanelProps {
  actividadId: string;
  tiempoRegistradoMin: number;
  compact?: boolean;
}

function formatMinutos(min: number): string {
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function getTodayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function TiempoPanel({ actividadId, tiempoRegistradoMin, compact = false }: TiempoPanelProps) {
  const { data: timer } = useTimerActivo();
  const iniciar = useIniciarTimer();
  const pausar = usePausarTimer();
  const reanudar = useReanudarTimer();
  const detener = useDetenerTimer();
  const { data: registros, isError: isRegistrosError, refetch: refetchRegistros } = useRegistrosTiempo(actividadId);
  const registrarManual = useRegistrarTiempoManual();
  const actualizarRegistro = useActualizarRegistroTiempo();
  const eliminarRegistro = useEliminarRegistroTiempo();

  // Manual form state
  const [showForm, setShowForm] = useState(false);
  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editMinutos, setEditMinutos] = useState('');
  const [editDescripcion, setEditDescripcion] = useState('');
  const [minutos, setMinutos] = useState('');
  const [fecha, setFecha] = useState(getTodayISO());
  const [descripcion, setDescripcion] = useState('');
  const [esFacturable, setEsFacturable] = useState(true);

  const { user } = useAuthStore();
  const canManualRegister = user?.roles?.includes('ADMIN') || user?.roles?.includes('SOPORTE');

  const isTimerOnThis = timer?.activo && timer.actividad_id === actividadId;
  const isTimerOnOther = timer?.activo && timer.actividad_id !== actividadId;
  const isPending = iniciar.isPending || pausar.isPending || reanudar.isPending || detener.isPending;

  const startEditing = (reg: { id: string; minutos: number; descripcion?: string | null }) => {
    setEditingId(reg.id);
    setEditMinutos(String(reg.minutos));
    setEditDescripcion(reg.descripcion || '');
  };

  const handleSaveEdit = (regId: string) => {
    const mins = parseInt(editMinutos, 10);
    if (!mins || mins <= 0) {
      toast.warning('Minutos invalidos');
      return;
    }
    actualizarRegistro.mutate(
      { id: regId, data: { minutos: mins, descripcion: editDescripcion } },
      { onSuccess: () => setEditingId(null) }
    );
  };

  const handleRegistrarManual = () => {
    const mins = parseInt(minutos, 10);
    if (!mins || mins <= 0) {
      toast.warning('Ingresa una cantidad de minutos valida');
      return;
    }
    registrarManual.mutate(
      {
        actividad_id: actividadId,
        fecha,
        minutos: mins,
        descripcion: descripcion || undefined,
        es_facturable: esFacturable,
      },
      {
        onSuccess: () => {
          setMinutos('');
          setDescripcion('');
          setShowForm(false);
        },
      }
    );
  };

  const Wrapper = compact ? 'div' : Card;

  return (
    <Wrapper className={compact ? '' : 'p-4'}>
      <div className="flex items-center justify-between mb-3">
        {!compact && (
          <h3 className="font-semibold flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Control de Tiempo
          </h3>
        )}
        <span className="text-sm font-medium text-muted-foreground">
          Total: {formatMinutos(tiempoRegistradoMin)}
        </span>
      </div>

      {/* Timer controls */}
      <div className="flex items-center gap-2 mb-4">
        {isTimerOnThis && timer ? (
          <>
            {timer.pausado ? (
              <Button
                size="sm"
                onClick={() => reanudar.mutate()}
                disabled={isPending}
              >
                <Play className="h-4 w-4 mr-1" />
                Reanudar
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={() => pausar.mutate()}
                disabled={isPending}
              >
                <Pause className="h-4 w-4 mr-1" />
                Pausar
              </Button>
            )}
            <Button
              size="sm"
              variant="destructive"
              onClick={() => detener.mutate()}
              disabled={isPending}
            >
              <Square className="h-4 w-4 mr-1" />
              Detener
            </Button>
            <span className="text-sm text-muted-foreground ml-1">
              {timer.pausado ? 'Pausado' : 'En curso'} ({timer.minutos_transcurridos}m)
            </span>
          </>
        ) : (
          <Button
            size="sm"
            onClick={() => iniciar.mutate(actividadId)}
            disabled={isPending}
          >
            <Play className="h-4 w-4 mr-1" />
            {isTimerOnOther ? 'Cambiar timer aqui' : 'Iniciar Timer'}
          </Button>
        )}
        {canManualRegister && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowForm(!showForm)}
          >
            <Plus className="h-4 w-4 mr-1" />
            Manual
          </Button>
        )}
      </div>

      {/* Manual form */}
      {showForm && (
        <div className="border rounded-md p-3 mb-4 space-y-3 bg-muted/30">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Minutos</label>
              <input
                type="number"
                min="1"
                value={minutos}
                onChange={(e) => setMinutos(e.target.value)}
                placeholder="60"
                className="w-full h-8 px-2 rounded-md border border-input bg-background text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Fecha</label>
              <input
                type="date"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
                className="w-full h-8 px-2 rounded-md border border-input bg-background text-sm"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Descripcion (opcional)</label>
            <input
              type="text"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              placeholder="Que hiciste?"
              className="w-full h-8 px-2 rounded-md border border-input bg-background text-sm"
            />
          </div>
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={esFacturable}
                onChange={(e) => setEsFacturable(e.target.checked)}
                className="rounded border-input"
              />
              Facturable
            </label>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setShowForm(false)}>
                Cancelar
              </Button>
              <Button
                size="sm"
                onClick={handleRegistrarManual}
                loading={registrarManual.isPending}
              >
                Registrar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Registros list */}
      {registros && registros.length > 0 && (
        <div className="space-y-1">
          <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
            Historial ({registros.length})
          </h4>
          <div className="divide-y max-h-64 overflow-y-auto">
            {registros.map((reg) => (
              <div key={reg.id} className="py-2 text-sm">
                {editingId === reg.id ? (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs text-muted-foreground">Minutos</label>
                        <input
                          type="number"
                          min="1"
                          value={editMinutos}
                          onChange={(e) => setEditMinutos(e.target.value)}
                          className="w-full h-7 px-2 rounded border border-input bg-background text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Descripcion</label>
                        <input
                          type="text"
                          value={editDescripcion}
                          onChange={(e) => setEditDescripcion(e.target.value)}
                          className="w-full h-7 px-2 rounded border border-input bg-background text-sm"
                          placeholder="..."
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        onClick={() => setEditingId(null)}
                        className="p-1 rounded text-muted-foreground hover:text-foreground"
                        title="Cancelar"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => handleSaveEdit(reg.id)}
                        className="p-1 rounded text-primary hover:bg-primary/10"
                        title="Guardar"
                      >
                        <Save className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{formatMinutos(reg.minutos)}</span>
                        <span className="text-muted-foreground text-xs">{reg.fecha}</span>
                        {reg.origen === 'TIMER' ? (
                          <Badge variant="outline" className="text-[10px] px-1 py-0 text-blue-600 border-blue-200">
                            <Timer className="h-2.5 w-2.5 mr-0.5" />Timer
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] px-1 py-0 text-orange-600 border-orange-200">
                            <PenLine className="h-2.5 w-2.5 mr-0.5" />Manual
                          </Badge>
                        )}
                        {reg.es_facturable && (
                          <Badge variant="outline" className="text-[10px] px-1 py-0">$</Badge>
                        )}
                      </div>
                      {reg.descripcion && (
                        <p className="text-xs text-muted-foreground truncate">{reg.descripcion}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-0.5 ml-2 shrink-0">
                      <button
                        onClick={() => startEditing(reg)}
                        className="p-1 rounded text-muted-foreground hover:text-primary hover:bg-primary/10"
                        title="Editar registro"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm('Eliminar este registro de tiempo?')) {
                            eliminarRegistro.mutate(reg.id);
                          }
                        }}
                        className="p-1 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                        title="Eliminar registro"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {isRegistrosError && (
        <p className="text-xs text-destructive">
          Error al cargar registros.{' '}
          <button onClick={() => refetchRegistros()} className="underline hover:no-underline">
            Reintentar
          </button>
        </p>
      )}

      {registros && registros.length === 0 && !showForm && !isRegistrosError && (
        <p className="text-xs text-muted-foreground">
          Sin registros de tiempo aun. Usa el timer o registra manualmente.
        </p>
      )}
    </Wrapper>
  );
}
